/**
 * Les trois verdicts d'un parent, en boutons de couleur.
 *
 * Hedi, le 27 septembre 2026 : *« Je veux que Juste, À moitié, Faux : soient vue
 * comme des boutons avec couleur vert, orange, rouge. »* Les trois restaient
 * jusque-là des boutons discrets identiques, et le choix disparaissait au
 * profit de la confirmation : rien à l'écran ne disait lequel on s'apprêtait à
 * poser.
 *
 * **Les trois boutons restent affichés pendant la confirmation**, celui choisi
 * plein et les deux autres atténués, avec `aria-pressed` : un lecteur d'écran
 * entend « Juste, enfoncé », et un parent daltonien lit le mot — la couleur
 * n'est jamais la seule information. Changer d'avis avant de confirmer se fait
 * en appuyant sur un autre bouton ; appuyer de nouveau sur le bouton choisi le
 * relâche.
 *
 * Le barème — 1,0 / 0,5 / 0,0 — n'est **pas** ici : il vit dans
 * `EntrainementVerdict.score`, côté serveur, et nulle part ailleurs. Cet écran
 * envoie le mot ; une seconde table de correspondance écrite dans la coquille
 * finirait par diverger de celle-là sans que rien n'échoue.
 */

export const VERDICTS = [
  { valeur: "juste", bouton: "Juste", pose: "juste" },
  { valeur: "moitie", bouton: "À moitié", pose: "à moitié juste" },
  { valeur: "faux", bouton: "Faux", pose: "faux" },
] as const;

export type Valeur = (typeof VERDICTS)[number]["valeur"];

/**
 * « Pas fait » — Hedi, le 27 septembre 2026 : *« un bouton : pas fait qui
 * renvoie l'exercice au pool d'exercices a piocher dedans la prochaine fois »*.
 * Ce n'est pas une note : le serveur n'écrit ni tentative ni observation, et
 * l'exercice redevient tirable. Proposé sur une ligne d'entraînement
 * seulement ; une copie de sujet rendue a, elle, été faite.
 */
export const PAS_FAIT = {
  valeur: "pas_fait",
  bouton: "Pas fait",
  pose: "pas fait",
} as const;

export type Choix = Valeur | (typeof PAS_FAIT)["valeur"];

export const DIT: Record<string, string> = Object.fromEntries(
  [...VERDICTS, PAS_FAIT].map((verdict) => [verdict.valeur, verdict.pose]),
);

/**
 * La phrase de confirmation, pure et exportée pour être testée. « Pas fait »
 * n'écrit rien dans la maîtrise : il dit ce qui arrive à l'exercice, pas une
 * note.
 */
export function confirmation(choix: Choix): string {
  if (choix === "pas_fait") {
    return "Marquer cet exercice comme pas fait ? Il quitte la feuille de l'enfant et revient dans les exercices à tirer pour un prochain entraînement. Cela ne se reprend pas.";
  }
  return `Noter cet exercice ${DIT[choix]} ? Cela ne se reprend pas.`;
}

export function ChoixDuVerdict<C extends Choix>({
  choisi,
  choisir,
  desactive = false,
  pasFait = false,
}: {
  choisi: C | null;
  choisir: (valeur: C | null) => void;
  desactive?: boolean;
  /** Ajoute « Pas fait » après les trois notes. */
  pasFait?: boolean;
}) {
  const boutons: readonly { valeur: Choix; bouton: string }[] = pasFait
    ? [...VERDICTS, PAS_FAIT]
    : VERDICTS;
  return (
    <div
      className={`verdicts${choisi ? " verdicts--choisi" : ""}`}
      role="group"
      aria-label="Verdict"
    >
      {boutons.map((verdict) => {
        const presse = choisi === verdict.valeur;
        return (
          <button
            key={verdict.valeur}
            type="button"
            className={`btn btn--verdict btn--${verdict.valeur}`}
            aria-pressed={presse}
            disabled={desactive}
            onClick={() => choisir(presse ? null : (verdict.valeur as C))}
          >
            {verdict.bouton}
          </button>
        );
      })}
    </div>
  );
}
